import { findFirstNonEmptyString, formatInTimeZone, notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { tracking } from "~/data";
import { useImageUtils } from "~/lib/hooks";
import type { topPicksWidgetItemArticle$key } from "~/queries/__generated__/topPicksWidgetItemArticle.graphql";

import {
  Container,
  Headline,
  Section,
  StyledBaseImage,
  StyledEntityLink,
  TitleBar,
  UpdateDate,
} from "./styles";
import { TopPicksPrioritySectionEntityIds } from "./types";

export type Props = {
  onClick: () => void;
  recommendationId: string;
  reference: topPicksWidgetItemArticle$key;
};

export const TopPicksWidgetItem: FunctionComponent<Props> = ({
  onClick,
  recommendationId,
  reference: reference_,
}) => {
  const { getResizedImageUrl } = useImageUtils();

  const content = useFragment(
    graphql`
      fragment topPicksWidgetItemArticle on Article {
        ...entityLink
        entityId
        headline
        socialHeadline
        updatedDate
        sections {
          value {
            entityId
            name
          }
        }
        images {
          title
          url
          isSlideshow
          style(filter: { style: "237x147" }) {
            url
          }
        }
      }
    `,
    reference_,
  );

  const renderSection = () => {
    const sections = (content.sections ?? [])
      .filter(section => !!section?.value?.length)
      .flatMap(section => section?.value?.filter(v => !!v));

    const section =
      sections.find(
        section => notEmpty(section) && TopPicksPrioritySectionEntityIds.includes(section.entityId),
      ) ?? first(sections);

    return section?.name ?? "";
  };

  const renderHeadline = () => findFirstNonEmptyString(content.socialHeadline, content.headline);

  const renderUpdateDate = () => {
    if (!content.updatedDate) return "";
    return formatInTimeZone(content.updatedDate, "d MMM yyyy");
  };

  const renderImage = () => {
    const images = content.images?.filter(img => notEmpty(img)) ?? [];
    const mainImage = images.find(img => !!img?.isSlideshow) ?? first(images);
    const source = getResizedImageUrl(mainImage?.style?.url ?? mainImage?.url ?? "", 76);
    const alt = mainImage?.title ?? "";
    return (
      <StyledBaseImage
        $aspectRatio="237 / 147"
        $height="auto"
        $objectFit="cover"
        $width="76px"
        alt={alt}
        src={source}
        disableLinkingData
      />
    );
  };

  return (
    <StyledEntityLink
      query={{
        campaign: `${content.entityId}_${recommendationId}`,
        // The following query string for widget type = "top-picks"
        // but may have more for other widget types
        module: tracking.module.TopPicks,
        pgtype: tracking.pageType.Article,
      }}
      reference={content}
      onClick={onClick}
    >
      <Container>
        <TitleBar />
        <Section>{renderSection()}</Section>
        <Headline>{renderHeadline()}</Headline>
        <UpdateDate>{renderUpdateDate()}</UpdateDate>
        {renderImage()}
      </Container>
    </StyledEntityLink>
  );
};

TopPicksWidgetItem.displayName = "TopPicksWidgetItem";
